/**
 * Auditor Certificate List Page
 *
 * Shows all certificates related to the auditor's completed audits.
 * Auditors can view certificate details and verification status.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Award, Search, Filter, CheckCircle2, Clock, AlertCircle, Eye } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { formatDate } from '../../utils/formatting';

interface Certificate {
  id: string;
  certificateNumber: string;
  companyName: string;
  certificateType: 'HALAL' | 'TOYYIBAN';
  status: 'active' | 'pending' | 'expired' | 'suspended';
  issuedDate: string;
  expiryDate: string;
  auditor: string;
  auditDate: string;
  scope: string;
}

export const AuditorCertificateListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setCertificates([
        {
          id: 'cert-001',
          certificateNumber: 'HALMAL-2025-001',
          companyName: 'ABC Food Manufacturing Sdn Bhd',
          certificateType: 'HALAL',
          status: 'active',
          issuedDate: '2025-12-25',
          expiryDate: '2027-12-25',
          auditor: 'Sarah Ahmad',
          auditDate: '2025-12-20',
          scope: 'Manufacturing of ready-to-eat frozen food products',
        },
        {
          id: 'cert-002',
          certificateNumber: 'HALMAL-2025-002',
          companyName: 'Premium Bakery Industries',
          certificateType: 'HALAL',
          status: 'pending',
          issuedDate: '2026-01-05',
          expiryDate: '2028-01-05',
          auditor: 'Sarah Ahmad',
          auditDate: '2025-12-28',
          scope: 'Bakery products and confectionery',
        },
        {
          id: 'cert-003',
          certificateNumber: 'HALMAL-2023-045',
          companyName: 'Global Ingredients Supply Co.',
          certificateType: 'HALAL',
          status: 'expired',
          issuedDate: '2023-11-15',
          expiryDate: '2025-11-15',
          auditor: 'Sarah Ahmad',
          auditDate: '2023-11-10',
          scope: 'Supply of halal food ingredients and raw materials',
        },
        {
          id: 'cert-004',
          certificateNumber: 'HALMAL-2024-089',
          companyName: 'Tasty Snacks Manufacturing',
          certificateType: 'HALAL',
          status: 'active',
          issuedDate: '2024-06-20',
          expiryDate: '2026-06-20',
          auditor: 'Sarah Ahmad',
          auditDate: '2024-06-15',
          scope: 'Snack food production and packaging',
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status: Certificate['status']) => {
    switch (status) {
      case 'active':
        return <Badge variant="success" dot>{t('auditor:certificates.status.active')}</Badge>;
      case 'pending':
        return <Badge variant="warning" dot>{t('auditor:certificates.status.pending')}</Badge>;
      case 'expired':
        return <Badge variant="error" dot>{t('auditor:certificates.status.expired')}</Badge>;
      case 'suspended':
        return <Badge variant="error" dot>{t('auditor:certificates.status.suspended')}</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: Certificate['status']) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-warning-600" />;
      case 'expired':
      case 'suspended':
        return <AlertCircle className="w-5 h-5 text-error-600" />;
    }
  };

  const isExpiringRoon = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 3600 * 24));
    return diffDays > 0 && diffDays <= 90; // Within 90 days
  };

  const filteredCertificates = certificates.filter((cert) => {
    const matchesSearch = cert.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cert.certificateNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || cert.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('auditor:certificates.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('auditor:certificates.title') },
        ]}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('auditor:certificates.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('auditor:certificates.title') },
      ]}
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:certificates.stats.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{certificates.length}</p>
              </div>
              <Award className="w-8 h-8 text-primary-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:certificates.stats.active')}</p>
                <p className="text-2xl font-bold text-success-600">
                  {certificates.filter(c => c.status === 'active').length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-success-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:certificates.stats.pending')}</p>
                <p className="text-2xl font-bold text-warning-600">
                  {certificates.filter(c => c.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-warning-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:certificates.stats.expiringRoon')}</p>
                <p className="text-2xl font-bold text-warning-600">
                  {certificates.filter(c => isExpiringRoon(c.expiryDate)).length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-warning-600" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('auditor:certificates.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">{t('auditor:certificates.filters.all')}</option>
                <option value="active">{t('auditor:certificates.filters.active')}</option>
                <option value="pending">{t('auditor:certificates.filters.pending')}</option>
                <option value="expired">{t('auditor:certificates.filters.expired')}</option>
                <option value="suspended">{t('auditor:certificates.filters.suspended')}</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Certificate List */}
      {filteredCertificates.length === 0 ? (
        <EmptyState
          icon={<Award className="w-12 h-12" />}
          title={t('auditor:certificates.emptyState.title')}
          description={t('auditor:certificates.emptyState.description')}
        />
      ) : (
        <div className="space-y-4">
          {filteredCertificates.map((cert) => (
            <Card
              key={cert.id}
              hover
              className="cursor-pointer transition-shadow"
              onClick={() => navigate(`/auditor/certificates/${cert.id}`)}
            >
              <CardBody>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Status Icon */}
                    <div className="mt-1">
                      {getStatusIcon(cert.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {cert.certificateNumber}
                        </h3>
                        {getStatusBadge(cert.status)}
                        {isExpiringRoon(cert.expiryDate) && cert.status === 'active' && (
                          <Badge variant="warning">
                            {t('auditor:certificates.expiringRoon')}
                          </Badge>
                        )}
                      </div>

                      <p className="text-base text-gray-900 mb-2 font-medium">{cert.companyName}</p>

                      <p className="text-sm text-gray-600 mb-3">{cert.scope}</p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">{t('auditor:certificates.issuedDate')}:</span>{' '}
                          {formatDate(cert.issuedDate)}
                        </div>
                        <div>
                          <span className="font-medium">{t('auditor:certificates.expiryDate')}:</span>{' '}
                          <span className={
                            cert.status === 'expired' || isExpiringRoon(cert.expiryDate)
                              ? 'text-warning-600 font-semibold'
                              : ''
                          }>
                            {formatDate(cert.expiryDate)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">{t('auditor:certificates.auditDate')}:</span>{' '}
                          {formatDate(cert.auditDate)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Eye className="w-4 h-4" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/auditor/certificates/${cert.id}`);
                    }}
                  >
                    {t('auditor:certificates.viewDetails')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </PageLayout>
  );
};
